'use client';

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { getSpacePoints } from '@/services/space-mange';
import { i18n } from '@/i18n';
export interface SpacePoints {
  status: string;
  space_type: string;
  detail: Array<{
    type: string;
    balance: string;
    name: string;
    description: string;
    children: Array<{
      type: string;
      balance: string;
      name: string;
      description: string;
    }>;
  }>;
  balance: string;
  spaceId: string;
}

interface SpacePointsContextType {
  spacePoints: SpacePoints | null;
  isLoading: boolean;
  error: string | null;
  refetch: (showLoading?: boolean) => Promise<void>;
}

const SpacePointsContext = createContext<SpacePointsContextType | undefined>(
  undefined,
);

interface SpacePointsProviderProps {
  children: ReactNode;
}

export const SpacePointsProvider: React.FC<SpacePointsProviderProps> = ({
  children,
}) => {
  const [spacePoints, setSpacePoints] = useState<SpacePoints | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSpacePoints = async (showLoading = true) => {
    if (showLoading) {
      setIsLoading(true);
    }
    setError(null);
    try {
      const res = await getSpacePoints();
      setSpacePoints(res);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : i18n.t('error:fetchSpacePointsFailed');
      setError(errorMessage);
      console.error('Failed to fetch space points:', err);
    } finally {
      if (showLoading) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchSpacePoints();
  }, []);

  const value: SpacePointsContextType = {
    spacePoints,
    isLoading,
    error,
    refetch: fetchSpacePoints,
  };

  // 积分event没有国际化，这里先注释掉
  // useEffect(() => {
  //   const handleApiNotify = (event: Event) => {
  //     const customEvent = event as CustomEvent;
  //     const eventData = customEvent?.detail;

  //     if (eventData?.event === 'quota') {
  //       const { value } = eventData;
  //       setSpacePoints(value);
  //     }
  //   };

  //   // Add event listener
  //   window.addEventListener('api-notify', handleApiNotify as EventListener);

  //   // Cleanup
  //   return () => {
  //     window.removeEventListener(
  //       'api-notify',
  //       handleApiNotify as EventListener,
  //     );
  //   };
  // }, []);

  return (
    <SpacePointsContext.Provider value={value}>
      {children}
    </SpacePointsContext.Provider>
  );
};

export const useSpacePointsContext = () => {
  const context = useContext(SpacePointsContext);
  if (context === undefined) {
    throw new Error('useSpacePoints must be used within a SpacePointsProvider');
  }
  return context;
};
